import styled, { DefaultTheme } from "styled-components";
import { Card, Row, Modal, Button, Typography, Avatar, Space, Descriptions, Alert, Select } from "antd";

const { Title } = Typography;

// Declare module augmentation for styled-components theme
declare module "styled-components" {
  export interface DefaultTheme {
    mode?: 'light' | 'dark';
  }
}

// Theme helper function
const getThemeValue = <T,>(props: { theme?: DefaultTheme }, darkValue: T, lightValue: T): T => {
  return props.theme?.mode === "dark" ? darkValue : lightValue;
};

// Styled Components
export const Container = styled.div`
  padding: 16px 16px 16px 0px;
  background: ${(props) => getThemeValue(props, "#171923", "transparent")};
  color: ${(props) => getThemeValue(props, "#fff", "inherit")};
`;

export const StyledCard = styled(Card)`
  border-radius: 8px;
  border: 1px solid ${(props) => getThemeValue(props, "#718096", "#DCDCDC")};
  margin-bottom: 24px;
  background: ${(props) => getThemeValue(props, "#1a202c", "#ffffff")};

  .ant-card-body {
    padding: 12px 12px 12px 12px;
    border-radius: 12px;
    background: ${(props) => getThemeValue(props, "#1a202c", "#ffffff")};
  }
`;

export const FilterCard = styled(StyledCard)`
  .ant-card-body {
    padding: 20px;
  }
`;

export const StatsRow = styled(Row)`
  margin-bottom: 24px;
  
  @media (min-width: 992px) {
    .ant-col {
      flex: 1 1 0% !important;
      max-width: none !important;
    }
  }
`;

export const StatCard = styled(Card)<{ cardType?: string }>`
  --title-color: ${(props) => {
    const type = props.cardType || 'default';
    const colors: Record<string, string> = {
      total: '#1890ff',
      active: '#389e0d',
      withRoles: '#1890ff',
      withoutRoles: '#d46b08',
      default: '#8c8c8c',
    };
    return colors[type] || colors.default;
  }};
  
  border-radius: 12px;
  width: 100%;
  height: 100%;
  border: 1px solid ${(props) => {
    const isDark = props.theme?.mode === "dark";
    const type = props.cardType || 'default';
    if (isDark) {
      const colors: Record<string, string> = {
        total: '#111a2c',
        active: '#162312',
        withRoles: '#111a2c',
        withoutRoles: '#2b1d0f',
        default: '#262626',
      };
      return colors[type] || colors.default;
    }
    const colors: Record<string, string> = {
      total: '#e6f4ff',
      active: '#f6ffed',
      withRoles: '#e6f4ff',
      withoutRoles: '#fff7e6',
      default: '#f0f0f0',
    };
    return colors[type] || colors.default;
  }};
  box-shadow: ${(props) => getThemeValue(props, "0 2px 8px rgba(0, 0, 0, 0.3)", "0 2px 8px rgba(0, 0, 0, 0.08)")};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  position: relative;
  background: ${(props) => getThemeValue(props, "#1a202c", "#ffffff")};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${(props) => {
      const type = props.cardType || 'default';
      const colors: Record<string, string> = {
        total: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
        active: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
        withRoles: 'linear-gradient(135deg, #1890ff 0%, #40a9ff 100%)',
        withoutRoles: 'linear-gradient(135deg, #fa8c16 0%, #d46b08 100%)',
        default: 'linear-gradient(135deg, #8c8c8c 0%, #595959 100%)',
      };
      return colors[type] || colors.default;
    }};
  }

  &:hover {
    transform: translateY(-6px);
    box-shadow: ${(props) => getThemeValue(props, "0 8px 24px rgba(0, 0, 0, 0.5)", "0 8px 24px rgba(0, 0, 0, 0.12)")};
    border-color: ${(props) => {
      const isDark = props.theme?.mode === "dark";
      const type = props.cardType || 'default';
      if (isDark) {
        const colors: Record<string, string> = {
          total: '#1a3a5c',
          active: '#274916',
          withRoles: '#1a3a5c',
          withoutRoles: '#4a2e0f',
          default: '#434343',
        };
        return colors[type] || colors.default;
      }
      const colors: Record<string, string> = {
        total: '#91d5ff',
        active: '#d9f7be',
        withRoles: '#91d5ff',
        withoutRoles: '#ffe7ba',
        default: '#d9d9d9',
      };
      return colors[type] || colors.default;
    }};
  }

  .ant-card-body {
    padding: 16px 18px;
    background: ${(props) => getThemeValue(props, "#1a202c", "#ffffff")};
  }

  .ant-statistic {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .ant-statistic-title {
    font-size: 13px;
    color: var(--title-color) !important;
    font-weight: 600;
    margin-bottom: 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    line-height: 1.4;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .ant-statistic-content {
    display: flex;
    align-items: center;
    gap: 16px;
    width: 100%;
    flex-wrap: wrap;
  }

  .ant-statistic-content-value {
    font-size: 32px;
    font-weight: 700;
    line-height: 1.2;
    flex: 1;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: ${(props) => getThemeValue(props, "#fff", "inherit")};
  }

  .ant-statistic-content-prefix {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: ${(props) => {
      const type = props.cardType || 'default';
      const colors: Record<string, string> = {
        total: 'linear-gradient(135deg, #e6f4ff 0%, #bae0ff 100%)',
        active: 'linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%)',
        withRoles: 'linear-gradient(135deg, #e6f4ff 0%, #bae7ff 100%)',
        withoutRoles: 'linear-gradient(135deg, #fff7e6 0%, #ffe7ba 100%)',
        default: 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)',
      };
      return colors[type] || colors.default;
    }};
    flex-shrink: 0;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    position: relative;
    
    .anticon {
      font-size: 22px;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      line-height: 1;
      margin: 0;
      padding: 0;
    }
  }
`;

export const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
  color: ${(props) => getThemeValue(props, "#fff", "inherit")};
`;

export const TopControlBar = styled.div`
  display: flex;
  align-items: center;
  padding-bottom: 16px;
  background: ${(props) => getThemeValue(props, "#1a202c", "#fff")};
  border-bottom: 1px solid ${(props) => getThemeValue(props, "#718096", "#f0f0f0")};
  gap: 16px;
  flex-wrap: wrap;
`;

export const SearchSection = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;

  .ant-input-search {
    flex: 1;
    min-width: 250px;
  }
`;

// RoleDetailsModal styled components
export const PermissionCard = styled(Card)`
  margin-bottom: 8px;
  border-radius: 6px;

  .ant-card-body {
    padding: 12px 16px;
  }
`;

export const UserCard = styled(Card)`
  margin-bottom: 8px;
  border-radius: 6px;

  .ant-card-body {
    padding: 12px 16px;
  }
`;

export const PermissionsList = styled.div`
  max-height: 300px;
  overflow-y: auto;
  margin-top: 16px;
`;

export const UsersList = styled.div`
  max-height: 200px;
  overflow-y: auto;
  margin-top: 16px;
`;

// Modal styled components
export const StyledModal = styled(Modal)`
  z-index: 2000 !important;

  .ant-modal-mask {
    z-index: 2000 !important;
    background-color: rgba(0, 0, 0, 0.45);
    backdrop-filter: blur(4px);
  }

  .ant-modal-wrap {
    z-index: 2000 !important;
  }

  .ant-modal-content {
    border-radius: 16px;
    overflow: hidden;
    box-shadow: ${(props) => getThemeValue(props, "0 20px 60px rgba(0, 0, 0, 0.5)", "0 20px 60px rgba(0, 0, 0, 0.15)")};
    background: ${(props) => getThemeValue(props, "#1a202c", "#ffffff")};
  }

  .ant-modal-header {
    border-bottom: 1px solid ${(props) => getThemeValue(props, "#434343", "#f0f0f0")};
    padding: 20px 24px;
    background: ${(props) => getThemeValue(props, "#1a202c", "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)")};
    border-radius: 16px 16px 0 0;

    .ant-modal-title {
      font-size: 20px;
      font-weight: 600;
      color: ${(props) => getThemeValue(props, "#fff", "#1a1a1a")};
    }
  }

  .ant-modal-body {
    max-height: 70vh;
    overflow-y: auto;
    padding: 24px;
    background: ${(props) => getThemeValue(props, "#1a202c", "#fafafa")};
  }

  .ant-modal-footer {
    border-top: 1px solid ${(props) => getThemeValue(props, "#434343", "#f0f0f0")};
    background: ${(props) => getThemeValue(props, "#1a202c", "#ffffff")};
    padding-top: 10px;
    border-radius: 0 0 16px 16px;
  }

  .ant-modal-close {
    top: 20px;
    right: 24px;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    transition: all 0.2s;
    color: ${(props) => getThemeValue(props, "#fff", "inherit")};

    &:hover {
      background-color: ${(props) => getThemeValue(props, "#4a5568", "#f5f5f5")};
    }
  }
`;

export const ModalFooter = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

export const CloseButton = styled(Button)`
  border: 0 !important;
  color: #222222 !important;
  font-weight: 500 !important;
  border-radius: 8px !important;
  height: 38px !important;
  font-size: 16px !important;
  padding: 8px 16px !important;
  background-color: #EDEDED !important;

  &:hover {
    color: #222222 !important;
    background-color: #DEDEDE !important;
  }
`;

export const SubmitButton = styled(Button)`
  background-color: #2D6CCA;
  font-weight: 500 !important;
  border-radius: 8px !important;
  font-size: 16px !important;
  height: 38px !important;

  &:hover {
    background-color: #1e4d8f !important;
  }
`;

export const UserInfo = styled.div`
  padding: 20px;
  background: ${(props) => getThemeValue(props, "#1a202c", "#ffffff")};
  border-radius: 12px;
  margin-bottom: 24px;
  box-shadow: ${(props) => getThemeValue(props, "0 2px 8px rgba(0, 0, 0, 0.3)", "0 2px 8px rgba(0, 0, 0, 0.04)")};
  border: 1px solid ${(props) => getThemeValue(props, "#434343", "#f0f0f0")};
  display: flex;
  align-items: center;
  gap: 16px;
  
  .user-name {
    font-size: 18px;
    font-weight: 600;
    color: ${(props) => getThemeValue(props, "#fff", "#1a1a1a")};
    margin-bottom: 4px;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  .user-email {
    font-size: 14px;
    color: ${(props) => getThemeValue(props, "#bfbfbf", "#8c8c8c")};
  }
`;

export const ModalContentWrapper = styled.div`
  background: ${(props) => getThemeValue(props, "#1f1f1f", "#fafafa")};
  min-height: 100%;
`;

export const SectionCard = styled.div`
  background: ${(props) => getThemeValue(props, "#1a202c", "#ffffff")};
  padding: 20px;
  border-radius: 12px;
  box-shadow: ${(props) => getThemeValue(props, "0 2px 8px rgba(0, 0, 0, 0.3)", "0 2px 8px rgba(0, 0, 0, 0.04)")};
  border: 1px solid ${(props) => getThemeValue(props, "#434343", "#f0f0f0")};
  margin-bottom: 24px;
`;

export const SectionTitle = styled(Title)`
  margin-bottom: 16px !important;
  color: ${(props) => getThemeValue(props, "#fff", "#1a1a1a")} !important;
  font-weight: 600 !important;
  font-size: 16px !important;
  display: flex;
  align-items: center;
  gap: 8px;

  &::before {
    content: '';
    width: 4px;
    height: 20px;
    background: linear-gradient(135deg, #2D6CCA 0%, #1e4d8f 100%);
    border-radius: 2px;
    display: inline-block;
  }
`;

export const InfoItem = styled.div`
  margin-bottom: 12px;
  
  strong {
    color: ${(props) => getThemeValue(props, "#bfbfbf", "#595959")};
    font-weight: 600;
    margin-right: 8px;
  }
  
  span {
    color: ${(props) => getThemeValue(props, "#fff", "#1a1a1a")};
  }
`;

export const RolesList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

export const RoleTag = styled(Space)`
  padding: 8px 12px;
  background: ${(props) => getThemeValue(props, "#111a2c", "#f0f5ff")};
  border: 1px solid ${(props) => getThemeValue(props, "#1a3a5c", "#d6e4ff")};
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  
  .anticon {
    color: #2D6CCA;
  }
  
  span {
    color: ${(props) => getThemeValue(props, "#fff", "#1a1a1a")};
    font-weight: 500;
  }
  
  .system-label {
    color: #722ed1;
    font-weight: 600;
  }
`;

export const InfoAlert = styled(Alert)`
  border-radius: 12px;
  border: none;
  box-shadow: 0 2px 8px rgba(24, 144, 255, 0.15);
  margin-top: 16px;
  
  .ant-alert-message {
    font-weight: 600;
    font-size: 14px;
  }
`;

export const UserAvatar = styled(Avatar)`
  background: linear-gradient(135deg, #2D6CCA 0%, #1e4d8f 100%);
  box-shadow: 0 4px 12px rgba(45, 108, 202, 0.25);
  font-weight: 600;
  font-size: 20px;
  flex-shrink: 0;
`;

export const UserDetails = styled.div`
  flex: 1;
`;

export const StyledDescriptions = styled(Descriptions)`
  background: ${(props) => getThemeValue(props, "#1a202c", "#ffffff")};
  padding: 20px;
  border-radius: 12px;
  box-shadow: ${(props) => getThemeValue(props, "0 2px 8px rgba(0, 0, 0, 0.3)", "0 2px 8px rgba(0, 0, 0, 0.04)")};
  border: 1px solid ${(props) => getThemeValue(props, "#434343", "#f0f0f0")};
  margin-bottom: 24px;

  .ant-descriptions-item-label {
    font-weight: 600;
    color: ${(props) => getThemeValue(props, "#bfbfbf", "#595959")};
    background: ${(props) => getThemeValue(props, "#1a202c", "#fafafa")} !important;
  }

  .ant-descriptions-item-content {
    color: ${(props) => getThemeValue(props, "#fff", "#1a1a1a")};
  }
`;

export const StyledSelect = styled(Select)`
  width: 100% !important;
  
  .ant-select-selector {
    border-radius: 8px !important;
    height: 38px !important;
    min-height: 38px !important;
    padding: 4px 11px !important;
    
    .ant-select-selection-item {
      line-height: 28px !important;
    }
    
    .ant-select-selection-placeholder {
      line-height: 28px !important;
    }
  }
`;

export const ActionIconButton = styled.div<{ iconColor?: string }>`
  align-items: center;
  border: 1px solid ${(props) => getThemeValue(props, "#434343", "#eee")};
  border-radius: 10px;
  display: flex;
  height: 30px;
  justify-content: center;
  padding: 8px;
  width: 30px;
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s;
  background: ${(props) => getThemeValue(props, "transparent", "inherit")};

  svg {
    transition: color 0.2s;
  }

  &:hover {
    background-color: ${(props) => {
      const iconColor = props.iconColor || "#2D6CCA";
      if (iconColor === "#f5222d") {
        return getThemeValue(props, "rgba(245, 34, 45, 0.2)", "rgba(245, 34, 45, 0.1)");
      }
      return getThemeValue(props, "rgba(45, 108, 202, 0.2)", "rgba(45, 108, 202, 0.1)");
    }} !important;

    svg {
      color: ${(props) => props.iconColor || "#2D6CCA"} !important;
    }
  }
`;

